import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TCCMap, DrawControl, drawingButtonsHandlers } from '@tcc/treadstone';
import { getMessage } from '@tcc/i18n';
import styles from './MapTCC.css';
import RecenterButton from '../../../components/Map/RecenterButton';
import {
  recenter,
  updateViewport,
  activateMap,
  togglePin,
} from '../../../actions/mapActions';
import { openModal } from '../../../actions/modalActions';
import { equalViewports } from '../../../utils/viewport';
import { isTrueColorEnabled } from '../../../selectors/fhaSelectors';
import { trackEvent } from '../../../actions/analyticsActions';
import PinButton from '../../../components/Map/PinButton';
import { REGION_REPORT } from '../../../constants/modal';
import { ScoutingMap } from '../../layers/scouting';
import RegionButton from '../../../components/Map/RegionButton';
import MessageBar from '../../../components/Map/MessageBar';
import { getGeoJSON } from '../../../utils/regionUtils';
import {
  FeatureFlagKey,
  FieldFeature,
  MapViewport,
  ModalMode,
  ModalType,
  Region,
  UnitOfMeasure,
  ModalSize
} from '../../../types';
import { useAppSelector } from '../../../utils';
import CrosshairImage from '../../../images/splitViewCrosshair.svg';
import {
  getCurrentViewport,
  getField,
  getIsPlacingPin,
  getIsRecenterButtonVisible,
  getUserPreferences,
  isSplitViewEnabledSelector,
  isFeatureAvailableInCountry,
  isFieldFeatureAvailable
} from '../../../selectors';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { TrueColorButton } from '..';

export type MapTCCProps = {
  index: number;
  mapLayer: React.ReactNode;
  showMap: boolean;
};

type MapTCCState = {
  regionMeta: {
    isRegionStarted: boolean,
    vertexDrawn: number,
    isRegionCreated: boolean,
  }[],
}

type ShapeTCCState = string;

const mountStyle: React.CSSProperties = {
  opacity: 1,
  transition: 'opacity 0.5s ease',
};

const unMountStyle: React.CSSProperties = {
  opacity: 0,
  transition: 'opacity 0.5s ease',
};

const defaultMapState: MapTCCState = {
  regionMeta: [
    {
      isRegionStarted: false,
      vertexDrawn: 0,
      isRegionCreated: false,
    },
    {
      isRegionStarted: false,
      vertexDrawn: 0,
      isRegionCreated: false,
    },
  ],
};

export const MapTCC = ({ index, mapLayer, showMap }: MapTCCProps) => {
  const dispatch = useDispatch();

  const [mapState, setMapState] = useState<MapTCCState>({ ...defaultMapState });

  const state = useAppSelector(state => state);
  const currentViewport = getCurrentViewport(state);
  const isPlacingPin = getIsPlacingPin(state);
  const isRecenterButtonVisible = getIsRecenterButtonVisible(state);
  const isSplitViewEnabled = isSplitViewEnabledSelector(state);
  const { unitsOfMeasureTemplateName } = getUserPreferences(state);
  const isRegionReportFeatureAvailable = isFeatureAvailableInCountry(state, FieldFeature.FIELD_REGION_REPORT);
  const fieldHasFieldRegionReportFeature = isFieldFeatureAvailable(state, FieldFeature.FIELD_REGION_REPORT);
  const isScoutingFeatureAvailable = isFeatureAvailableInCountry(state, FieldFeature.SCOUTING);
  const showScoutingFeatures = isFieldFeatureAvailable(state, FieldFeature.SCOUTING);

  const hasEnhancedScouting = useFeatureFlag(FeatureFlagKey.WEB_DMB_ENHANCED_SCOUTING)
  const field = getField(state);
  const isTrueColorButtonVisible = isTrueColorEnabled(state, { index });

  const isScoutingRegionsVisible = useFeatureFlag(FeatureFlagKey.SCOUTING_REGIONS);
  const isZoomEnhacementEnabled = useFeatureFlag(FeatureFlagKey.WEB_DMB_ZOOM_ENHANCEMENTS);
  const isCommercialPolicyFieldRegionReportFFEnabled = useFeatureFlag(FeatureFlagKey.COMMERCIAL_POLICY_FIELD_REGION_REPORT);
  const shouldShowRegionsPaywall = isCommercialPolicyFieldRegionReportFFEnabled && !fieldHasFieldRegionReportFeature;
  const mapRef = useRef<unknown>(null);
  const regionRef = useRef<Region | null>(null);
  const shapeRef = useRef<ShapeTCCState>('');
  const shape = shapeRef.current;
  const { regionMeta } = mapState;

  const updateMapViewport = useCallback((viewport: MapViewport) => {
    if (viewport.center && !equalViewports(viewport, currentViewport)) {
      if (currentViewport.zoom && currentViewport.zoom !== viewport.zoom) {
        if (currentViewport.zoom > viewport.zoom) {
          dispatch(trackEvent('map', 'zoomed_out', {
            zoomFrom: currentViewport.zoom,
            zoomTo: viewport.zoom,
          }));
        } else if (currentViewport.zoom < viewport.zoom) {
          dispatch(trackEvent('map', 'zoomed_in', {
            zoomFrom: currentViewport.zoom,
            zoomTo: viewport.zoom,
          }));
        }
      }
      dispatch(updateViewport(viewport));
    }
  }, [dispatch, currentViewport]);

  const handleOnClick = useCallback((e: { latlng: { lat: number, lng: number } }) => {
    if (!isPlacingPin) return;

    const { lat } = e.latlng;
    const { lng } = e.latlng;

    dispatch(openModal(index, null as never, {
      type: hasEnhancedScouting ? ModalType.ENHANCED_SCOUTING_FORM : ModalType.SCOUTING_FORM,
      lat,
      lng,
      mode: hasEnhancedScouting ? ModalMode.ADD_ENHANCED_PIN : ModalMode.ADD_PIN,
      size: hasEnhancedScouting ? ModalSize.M : ModalSize.S,
    }));
  }, [dispatch, index, isPlacingPin, hasEnhancedScouting]);

  const onDoneClick = useCallback(() => {
    // Click on the "edit" button of the drawing tools
    drawingButtonsHandlers('edit', index);
    // Then, click on the "saveEdit" button
    const saveIndex =
      index === 1 &&
        document.querySelectorAll('a[title="Save changes"]').length > 1
        ? 1
        : 0;
    drawingButtonsHandlers('saveEdit', saveIndex);
  }, [index]);

  const resetState = useCallback(() => {
    setMapState((prevMapState) => {
      const regionMeta = [...prevMapState.regionMeta];
      regionMeta[index] = {
        isRegionStarted: false,
        vertexDrawn: 0,
        isRegionCreated: false,
      };
      return {
        ...prevMapState,
        regionMeta,
      };
    });
    regionRef.current = null;
    shapeRef.current = '';
  }, [index]);

  const handleRecenter = useCallback(() => {
    dispatch(trackEvent('recenter', 'selected', {
      isOperationLevelMap: false,
    }));
    dispatch(recenter());
  }, [dispatch]);

  const onActivateMap = useCallback((params: { target: unknown }) => {
    dispatch(activateMap(params));
  }, [dispatch]);

  const onTogglePin = useCallback(() => {
    dispatch(togglePin());
  }, [dispatch]);

  const onEdited = async (e: { layers: { getLayers: () => string[] | undefined[] } }) => {
    const regionLayer = e.layers.getLayers()[0];
    let region: Region | null = regionRef.current;
    const shape: ShapeTCCState = shapeRef.current;
    if (regionLayer) {
      region = await getGeoJSON(regionLayer, shape);
    }

    dispatch(openModal(index, null as never, {
      type: REGION_REPORT,
      size: ModalSize.L,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore It seems we are mixing activities and regions. But we are ignoring it for now give it might be a braking change.
      activity: region,
      mode: ModalMode.ADD_REGION,
    }));

    resetState();
    drawingButtonsHandlers('clear', index);
    const clearIndex =
      index === 1 &&
        document.querySelectorAll('a[title="Clear all layers"]')
          .length > 1
        ? 1
        : 0;
    drawingButtonsHandlers('clearLayers', clearIndex);
  };

  const onCreated = async (e: { layerType: string, layer: { editing: { enable: () => boolean } } }) => {
    e.layer.editing.enable();
    const region = await getGeoJSON(e.layer, e.layerType);
    regionRef.current = region;
    setMapState((prevMapState) => {
      const newRegionMeta = [...prevMapState.regionMeta];
      newRegionMeta[index] = {
        ...newRegionMeta[index],
        isRegionCreated: true,
      };
      return {
        ...prevMapState,
        regionMeta: newRegionMeta,
      };
    });
  };

  const onDrawStart = (e: { layerType: string }) => {
    setMapState((prevMapState) => {
      shapeRef.current = e.layerType;
      const newRegionMeta = [...prevMapState.regionMeta];
      newRegionMeta[index] = {
        ...newRegionMeta[index],
        isRegionStarted: true,
      };
      return {
        ...prevMapState,
        regionMeta: newRegionMeta,
      };
    });
  };

  const onDrawStop = () => {
    setMapState((prevMapState) => {
      const newRegionMeta = [...prevMapState.regionMeta];
      newRegionMeta[index] = {
        ...newRegionMeta[index],
        isRegionStarted: false,
        vertexDrawn: 0,
      };
      return {
        ...prevMapState,
        regionMeta: newRegionMeta,
      };
    });
  };

  const onDrawVertex = () => {
    setMapState((prevMapState) => {
      const newRegionMeta = [...prevMapState.regionMeta];
      newRegionMeta[index] = {
        ...newRegionMeta[index],
        vertexDrawn: newRegionMeta[index].vertexDrawn + 1,
      };
      return {
        ...prevMapState,
        regionMeta: newRegionMeta,
      };
    });
  };

  const tooltipMessages = {
    circle: {
      start: getMessage('regionDraw.tooltip.circle.start'),
      radius: getMessage('regionDraw.tooltip.circle.radius'),
    },
    polygon: {
      start: getMessage('regionDraw.tooltip.polygon.start'),
      cont: getMessage('regionDraw.tooltip.polygon.cont'),
      end: getMessage('regionDraw.tooltip.circle.end'),
    },
    rectangle: {
      start: getMessage('regionDraw.tooltip.rectangle.start'),
    },
    simpleshape: {
      end: getMessage('regionDraw.tooltip.simpleshape.end'),
    },
    remove: {
      text: getMessage('regionDraw.tooltip.remove.text'),
    },
  };

  useEffect(() => {
    // We have to invalidate inner leaflet size on every update. If not,
    // map centering will not work properly.
    const map = (mapRef.current) as { leafletElement?: { invalidateSize?: (val: boolean) => void } } | null;
    if (map && map.leafletElement && map.leafletElement.invalidateSize) {
      map.leafletElement.invalidateSize(true);
    }
  });

  const className = `${styles.mapContainer} ${isSplitViewEnabled ? styles.mapContainerSplit : ''
    }`;

  const style: React.CSSProperties = {};

  if (isPlacingPin) {
    style.cursor = 'pointer';
  }

  const shapeUoM =
    unitsOfMeasureTemplateName === UnitOfMeasure.IMPERIAL
      ? {
        metric: false,
        feet: true,
      }
      : {
        metric: true,
        feet: false,
      };

  const showRegionFeature =
    isRegionReportFeatureAvailable && isScoutingFeatureAvailable && isScoutingRegionsVisible;

  const styleState = showMap ? mountStyle : unMountStyle;

  return (
    <div className={className} data-test-id="dmb-map" style={styleState}>
      <div className={styles.positionContainer}>
        <TCCMap
          bounds={field.boundary.bounds}
          className={styles.leafletContainer}
          key={`map-${field.id}`}
          minZoom={2}
          maxZoom={isZoomEnhacementEnabled ? 20 : undefined}
          onClick={handleOnClick}
          onViewportChanged={updateMapViewport}
          ref={mapRef}
          style={style}
          viewport={currentViewport}
          whenReady={onActivateMap}
        >
          {mapLayer}
          {showScoutingFeatures && <ScoutingMap index={index} />}
          {showScoutingFeatures && showRegionFeature && (
            <DrawControl
              draw={{
                polyline: false,
                marker: false,
                circlemarker: false,
                circle: {
                  ...shapeUoM,
                },
                rectangle: {
                  ...shapeUoM,
                },
              }}
              onCreated={onCreated}
              onDrawStart={onDrawStart}
              onDrawStop={onDrawStop}
              onDrawVertex={onDrawVertex}
              onEdited={onEdited}
              tooltipMessages={tooltipMessages}
            />
          )}
        </TCCMap>
        {isSplitViewEnabled && (
          <CrosshairImage className={styles.crosshair} data-test-id={`map-crosshair-${index}`} />
        )}
        {!regionMeta[index].isRegionStarted &&
          !regionMeta[index].isRegionCreated && (
            <div className={styles.mapActionButtonContainer} data-test-id="map-action-button-container">
              {isTrueColorButtonVisible && <TrueColorButton index={index} />}
              <RecenterButton
                onClick={handleRecenter}
                show={isRecenterButtonVisible}
              />
              {showScoutingFeatures && showRegionFeature && (
                <RegionButton
                  index={index}
                  showPaywall={shouldShowRegionsPaywall}
                />
              )}
              {showScoutingFeatures && <PinButton
                isPlacingPin={isPlacingPin}
                onClick={onTogglePin}
              />}
            </div>
          )}
      </div>
      <MessageBar
        index={index}
        onDoneClick={onDoneClick}
        regionMeta={regionMeta}
        resetState={resetState}
        shape={shape}
      />
    </div>
  );
};
